import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MdChevronLeft, MdSearch } from 'react-icons/md';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import axios from 'axios';
import ENDPOINTS from '../../api/endPoints';
import type { RootState } from '../../redux/store';
import { useCampaigns } from '../../hooks/useCampaigns';
import CampaignAnalyticsRow from './CampaignAnalyticsRow';

interface PublishDataItem {
  channel: string;
  postId: string;
  status: string;
  date?: string;
}

interface ChannelAnalytics {
  [key: string]: any;
}

const Analytics: React.FC = () => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelAnalytics, setChannelAnalytics] = useState<Record<string, Record<string, ChannelAnalytics>>>({});
  const [loadingAnalytics, setLoadingAnalytics] = useState<Record<string, Set<string>>>({});

  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  // Use smart campaigns hook - avoids redundant API calls
  const { campaigns: allCampaigns, isInitialLoading: loading, error } = useCampaigns();

  // Filter campaigns that have publishData with status "posted"
  const campaigns = useMemo(() => {
    return allCampaigns.filter((campaign: any) => {
      const publishData = campaign.publishData || campaign.publish_data;
      return publishData &&
        Array.isArray(publishData) &&
        publishData.length > 0 &&
        publishData.some((pd: any) => pd.status === 'posted');
    });
  }, [allCampaigns]);

  // Filter campaigns based on search query
  const filteredCampaigns = useMemo(() => {
    if (!searchQuery.trim()) {
      return campaigns;
    }
    return campaigns.filter((campaign) =>
      campaign.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [campaigns, searchQuery]);

  // Fetch analytics for a specific channel
  const fetchChannelAnalytics = async (campaignId: string, channel: string, postId: string, postedDate?: string) => {
    if (channelAnalytics[campaignId]?.[channel] || loadingAnalytics[campaignId]?.has(channel)) {
      return;
    }

    setLoadingAnalytics(prev => ({
      ...prev,
      [campaignId]: new Set([...(prev[campaignId] || []), channel])
    }));

    try {
      // For email, convert posted date to YYYY-MM-DD format
      let startDate: string | undefined;
      if (channel.toLowerCase() === 'email' && postedDate) {
        const date = new Date(postedDate);
        startDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      }

      const response = await axios.get(
        ENDPOINTS.channelAnalytics(channel, postId, campaignId, startDate),
        {
          headers: { 'restaurant-id': restaurantId },
        }
      );

      if (response.data?.success) {
        setChannelAnalytics(prev => ({
          ...prev,
          [campaignId]: {
            ...prev[campaignId],
            [channel]: response.data.data || {}
          }
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${channel} analytics:`, err);
      setChannelAnalytics(prev => ({
        ...prev,
        [campaignId]: {
          ...prev[campaignId],
          [channel]: {}
        }
      }));
    } finally {
      setLoadingAnalytics(prev => {
        const newSet = new Set(prev[campaignId] || []);
        newSet.delete(channel);
        return { ...prev, [campaignId]: newSet };
      });
    }
  };

  const toggleRow = (campaignId: string, publishData: PublishDataItem[]) => {
    const isExpanding = expandedRow !== campaignId;
    setExpandedRow(isExpanding ? campaignId : null);

    if (isExpanding) {
      publishData
        .filter(pd => pd.status === 'posted')
        .forEach(pd => fetchChannelAnalytics(campaignId, pd.channel, pd.postId, pd.date));
    }
  };

  // Loading/error states
  if (loading) return <LoadingState message="Loading campaign analytics..." />;
  if (error) return <ErrorState message={error} />;

  // Empty state
  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="fixed inset-0 left-0 md:left-[280px] flex items-center justify-center bg-black">
        <div className="flex flex-col items-center justify-center text-center px-4">
          <img
            src="/assets/images/emptyAnalytics.png"
            alt="No Analytics"
            className="w-48 h-48 md:w-60 md:h-60 mb-8 object-contain"
            onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
          />
          <h2 className="text-2xl font-medium text-white mb-3">No Analytics Available Yet</h2>
          <p className="text-gray-400 text-base mb-8 max-w-md leading-relaxed">
            Analytics will appear here once your campaigns<br />
            start running and performance data is collected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with Back Button and Search */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => window.history.back()}
              className="text-brand-primary cursor-pointer hover:text-gray-400 transition-colors"
            >
              <MdChevronLeft className="text-2xl" />
            </button>
            <h1 className="text-xl font-normal text-white">Campaign Analytics</h1>
          </div>

          {/* Search Input */}
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-surface-dark border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary transition-colors w-64"
            />
          </div>
        </div>
      </div>

      {/* No Results State */}
      {filteredCampaigns.length === 0 && searchQuery.trim() && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-400 text-lg mb-2">No campaigns found</p>
            <p className="text-gray-500 text-sm">Try adjusting your search query</p>
          </div>
        </div>
      )}

      {/* Table Container */}
      {filteredCampaigns.length > 0 && (
        <div className="rounded-lg border border-surface-dark max-h-[calc(100vh-180px)] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface-dark border-b border-surface-medium">
                <th className="text-left p-4 text-white font-semibold w-1/3 bg-surface-dark">Campaigns</th>
                <th className="text-left p-4 text-white font-semibold bg-surface-dark">Platforms</th>
                <th className="text-left p-4 text-white font-semibold bg-surface-dark">Posted On</th>
                <th className="text-left p-4 text-white font-semibold bg-surface-dark">Status</th>
                <th className="text-right p-4 text-white font-semibold w-32 bg-surface-dark">Duration</th>
              </tr>
            </thead>
            <tbody className='bg-[#0f0f0f]'>
              {filteredCampaigns.map((campaign: any, index: number) => {
                const publishData = campaign.publishData || campaign.publish_data || [];

                return (
                  <CampaignAnalyticsRow
                    key={campaign.id}
                    campaign={campaign}
                    index={index}
                    isExpanded={expandedRow === campaign.id}
                    onToggle={() => toggleRow(campaign.id, publishData)}
                    channelAnalytics={channelAnalytics}
                    loadingAnalytics={loadingAnalytics}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Analytics;
